import { useFirm } from "@app/pages/settings/useFirm";
import { CarPdfViewer } from "@app/components/PdfViewer";
import { useTheme } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { SimulatedForwardProbabilitiesPdfChartAC } from "./SimulatedForwardProbabilitiesPdfChart";
import { useSimulationSimulationJobLatestSimulationRetrieveQuery } from "@app/api/carApi.generated";
import { useAssetClassExtendedStat } from "../useAssetClassExtendedStat";
import { useSimulationGroups } from "../../assetAllocation/useSimulationGroups";
import { CarPdfDocument } from "@app/components/PdfDocument";
import { useSimulatedForwardProbabilitiesACData } from "../useSimulatedForwardProbabilitiesACData";
import { AssetClassLoadType } from "@app/features/assets/useAssetClasses";
import { assetClassAnalysisPages } from "@app/const/route.const";
import { useUser } from "@app/pages/settings/useUser";
import { CarPdfPage } from "@app/components/PdfPage";
import { SimulatedForwardProbabilitiesPdfChartLegend } from "./SimulatedForwardProbabilitiesPdfChartLegend";
import { CarPdfBox } from "@app/components/PdfBox";
import { CarPdfTypography } from "@app/components/PdfTypography";

export const ALL_ASSET_CLASSES_DATA_ID = "all";

export const PdfSimulatedForwardProbabilitiesReportPage = () => {
  const [searchParams] = useSearchParams();

  const {
    assetClass1Id,
    assetClass2Id,
    isReal,
    isAdvanced,
    chartDateRange1,
    chartDateRange2,
  } =
    assetClassAnalysisPages.getPdfSimulatedForwardProbabilitiesReportParams(
      searchParams,
    );

  const firm = useFirm();

  const latestSimulation =
    useSimulationSimulationJobLatestSimulationRetrieveQuery();

  const simulationGroups = useSimulationGroups();

  const getSimulationSuffix = (id?: string) =>
    simulationGroups.items.find((i) => i.latest_simulation?.id === id)
      ?.latest_simulation?.is_static
      ? "Static"
      : "Price Driven";

  const simulationJobId1 = latestSimulation.data?.id;
  const simulationJobId2 = simulationGroups.items.find(
    (i) =>
      i.latest_simulation?.id !== latestSimulation.data?.id &&
      i.latest_simulation?.is_static !== latestSimulation.data?.is_static,
  )?.latest_simulation?.id;

  const data1 = useAssetClassExtendedStat({
    type: AssetClassLoadType.bySimulationJobId,
    simulationJobId: simulationJobId1,
  });

  const data2 = useAssetClassExtendedStat({
    type: AssetClassLoadType.bySimulationJobId,
    simulationJobId: simulationJobId2,
  });

  const isLoading =
    firm.isLoading ||
    data1.isLoading ||
    data2.isLoading ||
    simulationGroups.isLoading ||
    latestSimulation.isLoading;

  const theme = useTheme();

  const { getChartData } = useSimulatedForwardProbabilitiesACData({
    nominalHeatmap1: data1.nominalHeatmap,
    realHeatmap1: data1.realHeatmap,
    simulationSuffix1: getSimulationSuffix(simulationJobId1),

    nominalHeatmap2: data2.nominalHeatmap,
    realHeatmap2: data2.realHeatmap,
    simulationSuffix2: getSimulationSuffix(simulationJobId2),

    isAdvanced,
    isReal,
    chartDateRange1,
    chartDateRange2,
  });

  type ChartData = ReturnType<typeof getChartData>;

  const footer = "Asset Class Analysis";

  const getChartPage = (chartData: ChartData) => (
    <CarPdfPage
      reportTitle="Simulated Forward Probabilities"
      sectionTitle="Forward Looking Analysis"
      footer={footer}
    >
      <CarPdfBox
        style={{
          marginTop: 36,
          flexDirection: "row",
          width: "100%",
          gap: 6,
        }}
      >
        <CarPdfTypography
          variant="v14Semibold"
          dense
          style={{ maxWidth: 306, lineHeight: 18 }}
        >
          {`${chartData.label1}; Time Frame ${chartDateRange1} Years (${isReal ? "Real" : "Nominal"})`}
        </CarPdfTypography>
        <SimulatedForwardProbabilitiesPdfChartLegend
          style={{ marginTop: 2, marginLeft: "auto" }}
        />
      </CarPdfBox>
      <SimulatedForwardProbabilitiesPdfChartAC
        style={{ marginTop: 26 }}
        data={chartData}
      />
    </CarPdfPage>
  );

  const isSingle = assetClass1Id !== ALL_ASSET_CLASSES_DATA_ID;

  const user = useUser();

  if (isSingle) {
    const chartData = getChartData({
      data1Id: assetClass1Id,
      data2Id: assetClass2Id,
    });

    const title = `Simulated Forward Probabilities ${chartData.label1} vs. ${chartData.label2}`;

    return (
      <CarPdfViewer isLoading={user.isLoading || isLoading}>
        <CarPdfDocument title={title} author={user.userFullName}>
          {getChartPage(chartData)}
        </CarPdfDocument>
      </CarPdfViewer>
    );
  } else {
    const title = `Simulated Forward Probabilities`;

    return (
      <CarPdfViewer isLoading={user.isLoading || isLoading}>
        <CarPdfDocument title={title} author={user.userFullName}>
          {data1.oneYearUnderPerf.map((i) =>
            getChartPage(
              getChartData({
                data1Id: i.id,
                data2Id: assetClass2Id,
              }),
            ),
          )}
        </CarPdfDocument>
      </CarPdfViewer>
    );
  }
};
